import createClient from 'openapi-fetch'
import { queryClient } from '../query-client'
import type { paths, components } from './schema'

export type Session = components['schemas']['Session']
export type User = components['schemas']['User']
export type LoginInput = NonNullable<
  paths['/api/auth/login']['post']['requestBody']
>['content']['application/json']
type ErrorBody = components['schemas']['Error']

export class ApiFailure extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly requestId?: string,
  ) {
    super(code)
  }
}

const client = createClient<paths>({
  credentials: 'same-origin',
  cache: 'no-store',
  fetch: async (request) => {
    try {
      return await fetch(request, {
        signal: AbortSignal.any([request.signal, AbortSignal.timeout(8000)]),
      })
    } catch (error) {
      if (request.signal.aborted) throw error
      throw new ApiFailure(0, 'API_UNAVAILABLE')
    }
  },
})

function unwrap<T>(result: {
  data?: T
  error?: ErrorBody
  response: Response
}): T {
  if (!result.response.ok)
    throw new ApiFailure(
      result.response.status,
      result.error?.error.code ?? 'API_UNAVAILABLE',
      result.error?.error.requestId,
    )
  if (result.data === undefined) throw new ApiFailure(0, 'API_UNAVAILABLE')
  return result.data
}
export async function getSession() {
  return unwrap(await client.GET('/api/auth/me'))
}
export async function login(input: LoginInput) {
  return unwrap(await client.POST('/api/auth/login', { body: input }))
}
export async function logout(csrfToken: string) {
  const result = await client.POST('/api/auth/logout', {
    params: { header: { 'X-CSRF-Token': csrfToken } },
  })
  if (!result.response.ok)
    throw new ApiFailure(
      result.response.status,
      result.error?.error.code ?? 'API_UNAVAILABLE',
      result.error?.error.requestId,
    )
}
export async function getFoundation() {
  return unwrap(await client.GET('/api/foundation'))
}
export async function getReadiness() {
  return unwrap(await client.GET('/api/health/ready'))
}

export function failureKind(
  error: unknown,
): 'expired' | 'unavailable' | 'rejected' {
  if (error instanceof ApiFailure && error.status === 401) return 'expired'
  if (
    !(error instanceof ApiFailure) ||
    error.status === 0 ||
    error.status >= 500
  )
    return 'unavailable'
  return 'rejected'
}
export function errorMessage(error: unknown): string {
  if (error instanceof ApiFailure) {
    if (error.code === 'INVALID_CREDENTIALS')
      return 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง'
    if (error.status === 429)
      return 'ลองเข้าสู่ระบบหลายครั้งเกินไป กรุณารอ 5 นาที'
    if (error.status === 403)
      return 'คำขอไม่ผ่านการตรวจสอบความปลอดภัย กรุณาลองใหม่'
  }
  return 'ติดต่อระบบไม่ได้ ข้อมูลในอุปกรณ์ยังคงอยู่ กรุณาลองใหม่เมื่อเชื่อมต่อได้'
}

export type Product = components['schemas']['Product']
export type ProductSummary = components['schemas']['ProductSummary']
export type SaleReceipt = components['schemas']['SaleReceipt']
export type SaleInput = components['schemas']['CreateSaleRequest']
export type ProductFilters = NonNullable<
  paths['/api/products']['get']['parameters']['query']
>
export async function getProducts(query: ProductFilters, signal?: AbortSignal) {
  return unwrapBusiness(
    await client.GET('/api/products', { params: { query }, signal }),
  )
}
export async function getCategories(signal?: AbortSignal) {
  return unwrapBusiness(await client.GET('/api/categories', { signal }))
}
export async function getProduct(id: string, signal?: AbortSignal) {
  return unwrapBusiness(
    await client.GET('/api/products/{id}', {
      params: { path: { id } },
      signal,
    }),
  )
}
export type UpdateProductInput = components['schemas']['UpdateProductRequest']
export type AdjustInventoryInput =
  components['schemas']['AdjustInventoryRequest']
export async function updateProduct(
  id: string,
  body: UpdateProductInput,
  csrfToken: string,
) {
  return unwrapBusiness(
    await client.PUT('/api/products/{id}', {
      body,
      params: { path: { id }, header: { 'X-CSRF-Token': csrfToken } },
    }),
  )
}
export async function deleteProduct(id: string, csrfToken: string) {
  const result = await client.DELETE('/api/products/{id}', {
    params: { path: { id }, header: { 'X-CSRF-Token': csrfToken } },
  })
  if (result.response.status === 401)
    void queryClient.invalidateQueries({ queryKey: ['auth', 'session'] })
  if (!result.response.ok)
    throw new ApiFailure(
      result.response.status,
      result.error?.error.code ?? 'API_UNAVAILABLE',
    )
}
export async function adjustInventory(
  body: AdjustInventoryInput,
  csrfToken: string,
  key: string,
) {
  return unwrapBusiness(
    await client.POST('/api/inventory/adjust', {
      body,
      params: { header: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': key } },
    }),
  )
}
export async function getDashboardReport(signal?: AbortSignal) {
  return unwrapBusiness(await client.GET('/api/reports/dashboard', { signal }))
}
export async function createSale(
  body: SaleInput,
  csrfToken: string,
  key: string,
) {
  return unwrapBusiness(
    await client.POST('/api/sales', {
      body,
      params: { header: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': key } },
    }),
  )
}
export function businessErrorMessage(error: unknown): string {
  if (error instanceof ApiFailure) {
    if (error.status === 429)
      return 'ระบบจำกัดคำขอชั่วคราว กรุณารอสักครู่แล้วลองใหม่'
    if (error.status === 401) return 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง'
    if (error.code === 'STALE_PRICE')
      return 'ราคาสินค้าเปลี่ยนแล้ว กรุณาตรวจราคาและเริ่มยืนยันบิลใหม่'
    if (error.code === 'INSUFFICIENT_STOCK')
      return 'สต๊อกไม่พอ กรุณาตรวจสอบจำนวนสินค้า'
    if (error.status === 400)
      return 'ข้อมูลบิลไม่ถูกต้องหรือล็อตหมดอายุ กรุณาตรวจสอบรายการ'
    if (error.status === 409) return 'ข้อมูลบิลขัดแย้ง กรุณาตรวจสอบก่อนลองใหม่'
  }
  return errorMessage(error)
}

function unwrapBusiness<T>(result: {
  data?: T
  error?: ErrorBody
  response: Response
}): T {
  if (result.response.status === 401)
    void queryClient.invalidateQueries({ queryKey: ['auth', 'session'] })
  return unwrap(result)
}

export type ReceiveInput = components['schemas']['ReceiveInventoryRequest']
export type ReceivedLot = components['schemas']['ProductLot']
export async function receiveInventory(
  body: ReceiveInput,
  csrfToken: string,
  key: string,
) {
  return unwrapBusiness(
    await client.POST('/api/inventory/receive', {
      body,
      params: { header: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': key } },
    }),
  )
}

export type CreateProductInput = components['schemas']['CreateProductRequest']
export async function createProduct(
  body: CreateProductInput,
  csrfToken: string,
  key: string,
) {
  return unwrapBusiness(
    await client.POST('/api/products', {
      body,
      params: { header: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': key } },
    }),
  )
}
